import { compact } from 'lodash';
import styled from '@emotion/styled';
import { mq } from '@styles/breakpoints';
import { ThemeProps } from '@theme/base';
import { TripTypeTourRowsList, TripTypeTours, ResponseLevel } from '@model/tours';
import { TILE_FALLBACK_IMAGE } from '@mock/fallback';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg';
import { LinkList, LinkRow } from './LinkList';
import { useI18NextContext } from '@components/hooks';
import { Amount, TripTypes } from '@model/common';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { mixins } from '@styles/mixins';

/* ***************** *
 *       Types       *
 * ***************** */

export interface LinkListGroupProps {
  tripType: TripTypes;
  tripTypeTours: TripTypeTours;
  images: Array<ImageProps>;
  heading: string;
}

export interface ImageProps {
  path: string;
  imageUrl: string;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const LinkListGroupContainer: any = styled.div(({ noOfColumns }: { noOfColumns: number }) => ({
  columnCount: 1,

  [mq.small]: {
    columnCount: noOfColumns < 2 ? noOfColumns : 2,
    width: noOfColumns < 2 ? '50%' : '100%'
  },

  [mq.medium]: {
    columnCount: noOfColumns < 3 ? noOfColumns : 3,
    width: noOfColumns < 2 ? '33%' : noOfColumns < 3 ? '50%' : '100%'
  }
}));

const LinkListItemContainer: any = styled.div(({ theme }: ThemeProps) => ({
  ...mixins(theme).card,
  width: '100%',
  breakInside: 'avoid',
  marginBottom: theme.custom.spacing.large,

  [mq.small]: {
    width: '95%'
  }
}));

const SubLabel: any = styled.span({});

const FromPriceLabel: any = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any)
}));

const FromPriceValue: any = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  fontWeight: 'bold'
}));

/* ************************* *
 *       LinkListGroup       *
 * ************************* */

export const LinkListGroup = ({ tripTypeTours, tripType, images, heading }: LinkListGroupProps) => {
  const t: any = useI18NextContext();
  const linkBuilder: DestinationLinkBuilder = new DestinationLinkBuilder();

  if (!Object.keys(tripTypeTours)?.length) {
    return null;
  }

  /* *** LOGIC *** */
  const getImage: (path: string) => string = (path: string) => {
    return images.find((image: ImageProps) => image.path === path)?.imageUrl || TILE_FALLBACK_IMAGE;
  };

  const getLink: (path: string, isTour: boolean) => LinkItem = (path: string, isTour: boolean) => {
    return isTour ? linkBuilder.getTourLink(path, tripType) : linkBuilder.getDestinationLink(path, tripType);
  };

  /* *** RENDERERS *** */
  const renderLinkListGroup = () => {
    return Object.keys(tripTypeTours).map((location: string) => {
      const tourRows: TripTypeTourRowsList = tripTypeTours[location]?.list || {};

      const links: Array<LinkRow> = compact(
        Object.keys(tourRows).map((tourRow: string) => {
          const isTour = tourRows[tourRow].pathType === ResponseLevel.TOUR;

          if (tripType === TripTypes.HOLIDAYS && !tourRows[tourRow].fromPrice.amount) {
            return null;
          }

          const subLabel: JSX.Element | null = tourRows[tourRow].fromPrice.amount ? (
            <SubLabel>
              <FromPriceLabel>{t('common__label--from')} </FromPriceLabel>
              <FromPriceValue>
                {new Amount(tourRows[tourRow].fromPrice.amount, tourRows[tourRow].fromPrice.currency.code).format()}
              </FromPriceValue>
            </SubLabel>
          ) : null;

          return {
            label: tourRow,
            subLabel,
            linkUrl: getLink(tourRows[tourRow].path, isTour)
          };
        })
      );

      if (!links.length) {
        return null;
      }

      const locationPath = tripTypeTours[location].path;

      return (
        <LinkListItemContainer key={location}>
          <LinkList
            imageUrl={getImage(tripTypeTours[location].path)}
            continent={{
              title: location,
              link: linkBuilder.getDestinationLink(locationPath, tripType).as
            }}
            links={links}
          />
        </LinkListItemContainer>
      );
    });
  };

  return (
    <>
      <SectionTitle size={SectionTitleSizes.LARGE} heading={t(heading)} />
      <LinkListGroupContainer noOfColumns={Object.keys(tripTypeTours).length}>
        {renderLinkListGroup()}
      </LinkListGroupContainer>
    </>
  );
};
